/**
 * Lógica de domínio pura do Gingado (sem dependência de storage).
 * Calcula derivados por atividade, tendência honesta com poucos dados, o efeito
 * regulatório tônico (acalmar/ativar), a autonomia (ajuda decrescente) e a fila de
 * rotina corporal.
 *
 * Espelha src/domain.ts (alimentar) e src/music/domain.ts (musical) — mesma
 * filosofia: não enganar com ruído, nunca rebaixar uma favorita marcada à mão,
 * honrar a escolha da criança, respeitar o que é sensível.
 */
import type { Tendencia } from "../types";
import {
  AtividadePsicomotora,
  Autonomia,
  DerivadosAtividade,
  EfeitoRegulatorio,
  EstadoRegulacao,
  MomentoPsicomotor,
  NivelAjuda,
  PassoParticipacao,
  StatusAtividade,
} from "./types";

const DIA_MS = 24 * 60 * 60 * 1000;

/** Nº mínimo de momentos pra arriscar uma direção/efeito. Abaixo disso: "poucos_dados". */
export const MIN_MOMENTOS_TENDENCIA = 3;

/**
 * Tendência honesta da participação. Com poucos pontos, NÃO promete direção.
 * Compara a média dos passos da metade mais recente vs. a mais antiga (limiar ±0,75).
 */
export function calcularTendenciaParticipacao(momentos: MomentoPsicomotor[]): Tendencia {
  const ords = momentos
    .slice()
    .sort((a, b) => a.data_hora.localeCompare(b.data_hora))
    .map((m) => m.passo_participacao as number);

  if (ords.length < MIN_MOMENTOS_TENDENCIA) return "poucos_dados";

  const meio = Math.floor(ords.length / 2);
  const antigos = ords.slice(0, meio);
  const recentes = ords.slice(ords.length - meio);
  const media = (xs: number[]) => xs.reduce((s, x) => s + x, 0) / xs.length;
  const delta = media(recentes) - media(antigos);

  if (delta >= 0.75) return "subindo";
  if (delta <= -0.75) return "caindo";
  return "estavel";
}

/** Eixo de ativação como número: baixo (-1) ↔ regulado (0) ↔ cima (+1). */
const AROUSAL: Record<EstadoRegulacao, number> = {
  desregulado_baixo: -1,
  regulado: 0,
  desregulado_cima: 1,
};

/**
 * Efeito regulatório tônico derivado: compara estado-antes vs. estado-depois dos
 * momentos que registraram AMBOS. Predominância (≥60%) define o rótulo; senão,
 * "variável". Idêntico ao Compasso — é o eixo compartilhado da timeline (§14).
 *  - acalma: baixa a ativação (depois < antes no eixo de arousal)
 *  - ativa:  sobe a ativação (depois > antes)
 *  - regula: traz para o centro (|depois| < |antes|), venha de cima ou de baixo
 */
export function calcularEfeitoRegulatorio(momentos: MomentoPsicomotor[]): EfeitoRegulatorio {
  const pares = momentos.filter(
    (m) => m.confirmado && m.estado_antes && m.estado_depois,
  );
  if (pares.length < MIN_MOMENTOS_TENDENCIA) return "poucos_dados";

  let acalma = 0; // baixou a ativação
  let ativa = 0; // subiu a ativação
  let aoCentro = 0; // chegou mais perto do "regulado"
  for (const m of pares) {
    const a = AROUSAL[m.estado_antes!];
    const d = AROUSAL[m.estado_depois!];
    if (d < a) acalma++;
    else if (d > a) ativa++;
    if (Math.abs(d) < Math.abs(a)) aoCentro++;
  }
  const total = pares.length;
  if (aoCentro / total >= 0.6 && acalma > 0 && ativa > 0) return "regula";
  if (acalma / total >= 0.6) return "acalma";
  if (ativa / total >= 0.6) return "ativa";
  if (aoCentro / total >= 0.6) return "regula";
  return "variavel";
}

/** Quanto de ajuda cada nível representa (0 = autônomo, 3 = ajuda física plena). */
const AJUDA_PESO: Record<NivelAjuda, number> = {
  nenhuma: 0,
  verbal: 1,
  fisica_leve: 2,
  fisica_plena: 3,
};

/**
 * Autonomia derivada (ajuda decrescente no tempo). Compara a média do PESO de ajuda
 * da metade recente vs. a antiga, só nos momentos que registraram nível de ajuda.
 * Menos ajuda = mais autonomia. Limiar de 0,5 para declarar "cresce".
 */
export function calcularAutonomia(momentos: MomentoPsicomotor[]): Autonomia {
  const comAjuda = momentos
    .filter((m) => m.confirmado && m.nivel_ajuda)
    .sort((a, b) => a.data_hora.localeCompare(b.data_hora))
    .map((m) => AJUDA_PESO[m.nivel_ajuda!]);

  if (comAjuda.length < MIN_MOMENTOS_TENDENCIA) return "poucos_dados";

  const meio = Math.floor(comAjuda.length / 2);
  const antigos = comAjuda.slice(0, meio);
  const recentes = comAjuda.slice(comAjuda.length - meio);
  const media = (xs: number[]) => xs.reduce((s, x) => s + x, 0) / xs.length;
  const delta = media(antigos) - media(recentes); // queda de ajuda = autonomia
  return delta >= 0.5 ? "cresce" : "estavel";
}

/** Deriva tudo que a UI precisa por atividade a partir dos seus momentos. */
export function calcularDerivadosAtividade(
  atividadeId: string,
  momentos: MomentoPsicomotor[],
  agora: Date = new Date(),
): DerivadosAtividade {
  const daAtiv = momentos
    .filter((m) => m.atividade_id === atividadeId && m.confirmado)
    .sort((a, b) => a.data_hora.localeCompare(b.data_hora));

  const ultimo = daAtiv[daAtiv.length - 1] ?? null;
  const passos = daAtiv.map((m) => m.passo_participacao as number);

  return {
    atividade_id: atividadeId,
    n_momentos: daAtiv.length,
    ultimo_momento: ultimo?.data_hora ?? null,
    passo_atual: (ultimo?.passo_participacao as PassoParticipacao) ?? null,
    passo_maximo: passos.length ? (Math.max(...passos) as PassoParticipacao) : null,
    tendencia: calcularTendenciaParticipacao(daAtiv),
    efeito_regulatorio: calcularEfeitoRegulatorio(daAtiv),
    autonomia: calcularAutonomia(daAtiv),
    dias_desde_ultimo: ultimo
      ? Math.floor((agora.getTime() - new Date(ultimo.data_hora).getTime()) / DIA_MS)
      : null,
  };
}

/**
 * Fila de rotina corporal (P0.6): atividades `em_exploracao` não usadas há >=
 * cadência. Ordena pelas mais "esquecidas" primeiro.
 */
export function filaRotinaCorporal(
  atividades: AtividadePsicomotora[],
  momentos: MomentoPsicomotor[],
  cadenciaDias: number,
  agora: Date = new Date(),
): Array<{ atividade: AtividadePsicomotora; dias_desde_ultimo: number | null }> {
  return atividades
    .filter((a) => a.status === "em_exploracao" && !a.arquivado)
    .map((a) => ({
      atividade: a,
      dias_desde_ultimo: calcularDerivadosAtividade(a.id, momentos, agora)
        .dias_desde_ultimo,
    }))
    .filter((x) => x.dias_desde_ultimo === null || x.dias_desde_ultimo >= cadenciaDias)
    .sort((a, b) => (b.dias_desde_ultimo ?? 1e9) - (a.dias_desde_ultimo ?? 1e9));
}

/**
 * Sugestão de status derivado. É só sugestão — o cuidador decide. Nunca rebaixa
 * uma `favorita` nem mexe numa `sensivel` marcada à mão (segurança sensorial/física).
 */
export function statusSugeridoAtividade(
  atual: StatusAtividade,
  d: DerivadosAtividade,
): StatusAtividade {
  if (atual === "favorita") return "favorita";
  if (atual === "sensivel") return "sensivel";
  if (d.n_momentos === 0) return "nao_usada";
  if ((d.passo_maximo ?? 0) >= PassoParticipacao.RealizacaoAutonoma) return "favorita";
  return "em_exploracao";
}

/**
 * Só pode entrar no modo escolha o que dá pra fazer com segurança agora: status
 * `favorita`/`em_exploracao`, não arquivado (honrar a escolha — P0.3).
 * `sensivel` fica de fora de propósito (respeitar aversões e risco físico).
 */
export function elegiveisEscolhaCorporal(
  atividades: AtividadePsicomotora[],
): AtividadePsicomotora[] {
  return atividades.filter(
    (a) => !a.arquivado && (a.status === "favorita" || a.status === "em_exploracao"),
  );
}

/** Cria um momento a partir de uma escolha da criança: origem fixa + pendente. */
export function momentoDaCrianca(
  base: Omit<MomentoPsicomotor, "origem" | "confirmado">,
): MomentoPsicomotor {
  return { ...base, origem: "crianca", confirmado: false };
}
